import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '../components/Button';
import Card from '../components/Card';
import SectionHeading from '../components/SectionHeading';

const MCP_SERVER_URL =
  process.env.REACT_APP_MCP_SERVER_URL || 'https://api.pay2rent.me/mcp';

const ConnectPage: React.FC = () => {
  const [copied, setCopied] = useState(false);

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(MCP_SERVER_URL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="space-y-16">
      <Helmet>
        <title>Connect your AI assistant | Pay2Rent.me</title>
      </Helmet>

      <section>
        <SectionHeading
          eyebrow="AI-native"
          title="Connect your AI assistant"
          subtitle="Manage your listings, availability, and bookings by asking. Paste one URL, sign in once, and your assistant can do the rest."
          as="h1"
        />
        <Card className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" interactive>
          <code className="break-all text-sm text-gray-900">{MCP_SERVER_URL}</code>
          <Button type="button" variant="secondary" size="sm" onClick={copyUrl}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Card>
      </section>

      <section>
        <SectionHeading title="Claude" align="left" />
        <ol className="mt-6 list-decimal space-y-2 pl-6 text-gray-700">
          <li>
            Open Claude and go to <strong>Customize &gt; Connectors</strong>.
          </li>
          <li>
            Click <strong>+</strong>, then <strong>Add custom connector</strong>.
          </li>
          <li>Paste the URL above.</li>
          <li>
            Click <strong>Add</strong>, then <strong>Connect</strong>, and sign in with your
            Pay2Rent.me account.
          </li>
          <li>
            In any chat, open <strong>+ &gt; Connectors</strong> and switch on Pay2Rent.me.
          </li>
        </ol>
        <p className="mt-4 text-sm text-gray-600">
          On Team and Enterprise plans an Owner adds the connector once under Organization
          settings &gt; Connectors, then each member clicks Connect.
        </p>
      </section>

      <section>
        <SectionHeading title="ChatGPT" align="left" />
        <ol className="mt-6 list-decimal space-y-2 pl-6 text-gray-700">
          <li>
            Open <strong>Settings &gt; Security and login</strong> and turn on{' '}
            <strong>Developer mode</strong>.
          </li>
          <li>
            Go to <strong>Plugins</strong> and click <strong>+</strong>.
          </li>
          <li>
            Give it a name and description, then paste the URL above under{' '}
            <strong>Connection</strong>.
          </li>
          <li>Create the connection and sign in with your Pay2Rent.me account.</li>
          <li>Start a chat and add Pay2Rent.me from the tools menu.</li>
        </ol>
      </section>

      <section>
        <SectionHeading title="Cursor and VS Code" align="left" />
        <pre className="mt-6 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
          {`{
  "mcpServers": {
    "pay2rent": {
      "type": "http",
      "url": "${MCP_SERVER_URL}"
    }
  }
}`}
        </pre>
      </section>

      <section>
        <SectionHeading title="Claude Code" align="left" />
        <pre className="mt-6 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
          {`claude mcp add --transport http pay2rent ${MCP_SERVER_URL}`}
        </pre>
      </section>

      <section>
        <SectionHeading title="What you can ask" align="left" />
        <Card className="mt-6">
          <ul className="space-y-2 text-gray-700">
            <li>List my listings</li>
            <li>Create a parking listing at 200 E Washington St called downtown-driveway</li>
            <li>Open weekday 9 to 5 availability at $8 an hour</li>
            <li>What&apos;s booked this week?</li>
          </ul>
        </Card>
      </section>

      <section>
        <SectionHeading title="What it cannot do" align="left" />
        <p className="mt-6 text-gray-700">
          Payout setup runs through Stripe in a browser. Your assistant can hand you the
          link, but you finish it yourself.
        </p>
      </section>

      <section>
        <SectionHeading title="Troubleshooting" align="left" />
        <ul className="mt-6 list-disc space-y-2 pl-6 text-gray-700">
          <li>
            Your assistant asks you to sign in again when the connection has been idle —
            reconnect from the same screen you added it on.
          </li>
          <li>
            A new listing takes no bookings until it has availability, so ask for opening
            hours right after creating one.
          </li>
          <li>
            Removing access means removing the connector in your assistant&apos;s settings.
          </li>
        </ul>
      </section>
    </div>
  );
};

export default ConnectPage;
